import React, { Dispatch, SetStateAction, useEffect, useRef } from "react"
import clsx from "clsx"
import { useForm, Controller } from "react-hook-form"

import { IllustrationData } from "src/models/tieup/illustration/IllustrationData"
import { Genre } from "src/models/genre/Genre"
import { HashTag } from "src/models/tieup/HashTag"
import GenreSelector from "src/components/common/GenreSelector"
import HashTags from "src/components/common/HashTags"
import ImageEditorDialog from "src/components/common/ImageEditorDialog"
import { useImageEditor } from "src/components/hooks/useImageEditor"
import AgeRating from "src/components/common/AgeRating"
import {
  IllustrationFormModel,
  makeEditIllustrationData,
  makeFormModel,
} from "./FormModel"
import { EditIllustrationData } from "../../../../../models/tieup/illustration/EditlIllustrationData"
import { getTieupImgSrc } from "../../../../common/TieupImg/getTieupImgSrc"

type Props = {
  onSubmit: (values: EditIllustrationData) => Promise<void>
  onUploadImage: (file: File, base64: string) => Promise<void>
  tmpImage: string
  setTmpImage?: Dispatch<SetStateAction<string>>
  data?: IllustrationData
  selectedRating?: string
  setSelectedRating?: (arg: string) => void
}

const DetailForm: React.FC<Props> = ({
  onSubmit,
  onUploadImage,
  tmpImage,
  data,
}) => {
  const { register, handleSubmit, errors, reset, control, setValue } =
    useForm<IllustrationFormModel>({
      defaultValues: {
        status: "true",
        rating: "all",
      },
    })

  /** 画像のinput file */
  const inputFileRef = useRef<HTMLInputElement>(null)

  /** 画像編集ダイヤログ */
  const { openImageEditorDialog, closeImageEditorDialog, imageSrc } =
    useImageEditor()

  /** 画像ファイルを選択 */
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length <= 0) return
    // 画像ダイヤログを表示
    openImageEditorDialog(e.target.files[0])
  }

  /** input fileのvalue要素を消す */
  const resetInputFileValue = () => {
    if (!inputFileRef || !inputFileRef.current) return
    inputFileRef.current.value = ""
  }

  useEffect(() => {
    if (data) {
      reset(makeFormModel(data))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, reset])

  return (
    <form
      onSubmit={handleSubmit(async (formModel) => {
        await onSubmit(makeEditIllustrationData(formModel))
      })}
    >
      <div className="creatorMypage-illustEdit-image">
        <label className="c-label-primary">
          イラストイメージ
          <span className="c-required-label">必須</span>
        </label>
        <ImageEditorDialog
          image={imageSrc}
          aspect={1 / 1.414}
          onUpload={onUploadImage}
          onClose={closeImageEditorDialog}
          title="指定された枠内で1（横）:1.414（縦）の比率でトリミングされます"
        />
        <div className="creatorMypage-illustEdit-image-upload">
          <label className="c-upload-button">
            <input
              className="c-upload-button-input"
              type="file"
              ref={inputFileRef}
              accept="image/png,image/jpeg"
              onChange={onFileChange}
              onClick={resetInputFileValue}
            />
            <span className="c-upload-button-text">画像アップロード</span>
          </label>
        </div>
        {!tmpImage && !data?.url && (
          <div className="creatorMypage-illustEdit-image-preview">
            <p className="creatorMypage-illustEdit-image-none">
              ※画像をアップロードしてください。
            </p>
          </div>
        )}
        {!!tmpImage && (
          <div className="creatorMypage-illustEdit-image-preview">
            <img src={getTieupImgSrc(tmpImage)} alt="" />
          </div>
        )}
      </div>
      <div className="u-spacer-sm">
        <label
          htmlFor="input-title"
          className="c-label-primary"
          style={{ alignItems: "center" }}
        >
          イラストタイトル
          <span className="c-label-primary-note">※15文字以内</span>
          <span className="c-required-label">必須</span>
        </label>
        <input
          id="input-title"
          type="text"
          name="title"
          className={clsx({
            "c-textfield-primary": true,
            "-small": true,
            "-error": errors.title,
          })}
          ref={register({ required: true, maxLength: 15 })}
        />
        {errors.title && (
          <p
            className={clsx({
              "c-note-primary": true,
              "-error": errors.title,
            })}
          >
            ※イラストタイトルは1文字以上15文字以下で入力してください。
          </p>
        )}
      </div>
      <section className="creatorMypage-illustEdit-section">
        <h2 className="creatorMypage-illustEdit-section-title">
          キャラクター設定
        </h2>
        <div className="u-spacer-sm">
          <label htmlFor="input-character-name" className="c-label-primary">
            名前
            <span className="c-label-primary-note">※15文字以内</span>
          </label>
          <input
            id="input-character-name"
            type="text"
            name="extension.chara_name" // { extension: { chara_name: string } } が生成される
            className={clsx({
              "c-textfield-primary": true,
              "-small": true,
              "-error": errors.extension?.chara_name,
            })}
            ref={register({ required: false, maxLength: 15 })}
          />
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="input-few_word" className="c-label-primary">
            ヒトコト台詞
            <span className="c-label-primary-note">※30文字以内</span>
          </label>
          <input
            id="input-few_word"
            type="text"
            name="extension.few_word"
            className={clsx({
              "c-textfield-primary": true,
              "-error": errors.extension?.few_word,
            })}
            ref={register({ required: false, maxLength: 30 })}
          />
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="input-interests" className="c-label-primary">
            趣味
            <span className="c-label-primary-note">※30文字以内</span>
          </label>
          <input
            id="input-interests"
            type="text"
            name="extension.interests"
            className={clsx({
              "c-textfield-primary": true,
              "-error": errors.extension?.interests,
            })}
            ref={register({ required: false, maxLength: 30 })}
          />
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="input-favorite-food" className="c-label-primary">
            好きな食べ物
            <span className="c-label-primary-note">※30文字以内</span>
          </label>
          <input
            id="input-favorite-food"
            type="text"
            name="extension.favorite_food"
            className={clsx({
              "c-textfield-primary": true,
              "-error": errors.extension?.favorite_food,
            })}
            ref={register({ required: false, maxLength: 30 })}
          />
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="input-affiliation" className="c-label-primary">
            所属
            <span className="c-label-primary-note">※30文字以内</span>
          </label>
          <input
            id="input-affiliation"
            type="text"
            name="extension.affiliation"
            className={clsx({
              "c-textfield-primary": true,
              "-error": errors.extension?.affiliation,
            })}
            ref={register({ required: false, maxLength: 30 })}
          />
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="input-illust-text" className="c-label-primary">
            説明
            <span className="c-label-primary-note">※1,000文字以内</span>
          </label>
          <textarea
            id="input-illust-text"
            name="extension.chara_description"
            className={clsx({
              "c-textarea-primary": true,
              "-height-medium-large": true,
              "-error": errors.extension?.chara_description,
            })}
            ref={register({ required: false, maxLength: 1000 })}
          />
        </div>
      </section>
      <section className="creatorMypage-illustEdit-section">
        <h2 className="creatorMypage-illustEdit-section-title -genre">
          ジャンル
          <span className="c-label-primary-note">※最大3点まで複数選択可</span>
        </h2>
        <div>
          <label htmlFor="" className="c-label-primary -genre">
            ジャンル
          </label>
          <div className="p-genreList">
            <Controller
              name="genres"
              control={control}
              defaultValue={[]}
              render={({ value }) => (
                <GenreSelector
                  isShowCategoryTitle={true}
                  selected={value}
                  onSubmit={(newSelected: Genre[]) => {
                    setValue("genres", newSelected)
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="input-original-genres" className="c-label-primary">
            オリジナルジャンル
            <span className="c-label-primary-note">※15文字以内</span>
          </label>
          <input
            id="input-original-genres"
            className={clsx({
              "c-textfield-primary": true,
              "-small": true,
              "-error": errors.original_genres,
            })}
            type="text"
            name="original_genres"
            ref={register({ maxLength: 15 })}
          />
        </div>
        <div className="u-spacer-sm">
          <label htmlFor="" className="c-label-primary">
            タグの入力
          </label>
          <Controller
            name="hashtags"
            control={control}
            defaultValue={[]}
            render={({ value }) => (
              <HashTags
                hashtags={value}
                onSubmit={(newTags: Partial<HashTag>[]) => {
                  setValue("hashtags", newTags)
                }}
              />
            )}
          />
        </div>
      </section>
      <section className="creatorMypage-illustEdit-section">
        <h2 className="creatorMypage-illustEdit-section-title">レイティング</h2>
        <div className="u-spacer-sm">
          <label className="c-label-primary">
            セルフレイティング
            <span className="c-label-primary-note">
              ※作品が以下の内容に該当する場合は選択してください。
            </span>
          </label>
          <ul className="p-inlineList">
            <li>
              <label className="c-checkbox-secondary">
                <input
                  className="c-checkbox-secondary-input"
                  type="checkbox"
                  name="is_grotesque"
                  ref={register}
                />
                <span className="c-checkbox-secondary-text">
                  残酷な描写あり
                </span>
              </label>
            </li>
            <li>
              <label className="c-checkbox-secondary">
                <input
                  className="c-checkbox-secondary-input"
                  type="checkbox"
                  name="is_violence"
                  ref={register}
                />
                <span className="c-checkbox-secondary-text">暴力描写あり</span>
              </label>
            </li>
            <li>
              <label className="c-checkbox-secondary">
                <input
                  className="c-checkbox-secondary-input"
                  type="checkbox"
                  name="is_sexual"
                  ref={register}
                />
                <span className="c-checkbox-secondary-text">性描写あり</span>
              </label>
            </li>
          </ul>
          <p className="c-note-primary">
            ※作品を読まれる方が不必要に不快な思いをされないように適切な設定をお願いいたします。
          </p>
        </div>

        {/* 年齢レイティング */}
        <div className="u-spacer-sm">
          <label htmlFor="" className="c-label-primary">
            年齢レイティング
            <span className="c-label-primary-note">
              ※作品の年齢レイティングを指定してください。
            </span>
          </label>
          <Controller
            name="rating"
            control={control}
            defaultValue={"all"}
            render={({ value, onChange }) => (
              <AgeRating
                ageRatings={[
                  { id: "all", label: "全年齢" },
                  { id: "r15", label: "R-15" },
                  { id: "r18", label: "R-18" },
                ]}
                selectedAgeId={value}
                onChange={(rate) => {
                  onChange(rate.id)
                }}
              />
            )}
          />
        </div>

        <div className="u-spacer-sm">
          <label htmlFor="" className="c-label-primary">
            公開ステータス
          </label>
          <ul className="p-inlineList">
            <li>
              <label className="c-radio-primary">
                <input
                  className="c-radio-primary-input"
                  type="radio"
                  name="status"
                  value="true"
                  ref={register}
                />
                <span className="c-radio-primary-text">公開</span>
              </label>
            </li>
            <li>
              <label className="c-radio-primary">
                <input
                  className="c-radio-primary-input"
                  type="radio"
                  name="status"
                  value="false"
                  ref={register}
                />
                <span className="c-radio-primary-text">非公開</span>
              </label>
            </li>
          </ul>
        </div>
      </section>

      <div className="p-button">
        <button className="c-button-secondary -full">保存する</button>
      </div>
    </form>
  )
}

export default DetailForm
